/**
 * FiberTopologyDialog2 - Hierarchical GPON Fiber Topology Visualization
 * Shows splitters as separate nodes (not inside poles)
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Panel,
  useReactFlow,
  getNodesBounds,
  getViewportForBounds,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toPng, toSvg } from 'html-to-image';

import { OLTNode } from '../FiberTopology/OLTNode';
import { SplitterNode } from '../FiberTopology/SplitterNode';
import { ONUNode } from '../FiberTopology/ONUNode';
import { FiberEdge } from '../FiberTopology/FiberEdge';
import { Pole, Cable, SplitterType } from '../../types';
import { useProjectStore, usePoleStore, useCableStore, useONUStore } from '../../stores';
import { getLayoutedElements } from '../../utils/layoutUtils';
import { traceOpticalPath } from '../../utils/opticalPathTracer';
import { getSplitterLoss, getSplitterOutputLoss } from '../../utils/splitterCatalog';
import { exportFlowToPng, exportFlowToSvg } from '../../utils/flowExport';
import { api } from '../../services/api';

interface FiberTopologyDialog2Props {
  open: boolean;
  onClose: () => void;
}

const nodeTypes = {
  olt: OLTNode,
  splitter: SplitterNode,
  onu: ONUNode,
};

const edgeTypes = {
  fiber: FiberEdge,
};

const SPLITTER_LOSS: Record<SplitterType, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 13.5,
  '1:32': 17.0,
  '1:64': 20.5,
};

const FiberTopologyContent2: React.FC<FiberTopologyDialog2Props> = ({ open, onClose }) => {
  const { currentProject } = useProjectStore();
  const { poles } = usePoleStore();
  const { cables } = useCableStore();
  const { onus } = useONUStore();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [showONU, setShowONU] = useState(true);
  const [showCustomerSplitters, setShowCustomerSplitters] = useState(true);
  const [showCableInfo, setShowCableInfo] = useState(true);
  const [showCableDistance, setShowCableDistance] = useState(true);
  const [showPassthrough, setShowPassthrough] = useState(true);
  const [showSplice, setShowSplice] = useState(true);
  const [showFiberColor, setShowFiberColor] = useState(true);
  const { getNodes, fitView } = useReactFlow();
  const [isExporting, setIsExporting] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showInfoMenu, setShowInfoMenu] = useState(false);

  // Path filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPath, setFilteredPath] = useState<Set<string> | null>(null); // Set of node IDs to show
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [showFullPath, setShowFullPath] = useState(false); // false = len po stĺpik, true = celá trasa

  // Load saved node positions from server
  const loadNodePositions = useCallback(() => {
    if (!currentProject) return {};
    const layout = currentProject.settings?.topology2_layout;
    return layout?.positions || {};
  }, [currentProject]);

  // Save node positions to server
  const saveNodePositions = useCallback(async (nodes: Node[]) => {
    if (!currentProject) return;
    const positions: Record<string, { x: number; y: number }> = {};
    nodes.forEach(node => {
      positions[node.id] = { x: node.position.x, y: node.position.y };
    });

    try {
      await api.projects.updateTopology2Layout(currentProject.id, { positions });
      console.log('[Topology2] Layout saved to server');
    } catch (error) {
      console.error('[Topology2] Failed to save layout:', error);
    }
  }, [currentProject]);

  // Custom onNodesChange handler that saves positions
  const handleNodesChange = useCallback((changes: any) => {
    onNodesChange(changes);
    // Save positions after drag ends
    const hasDragStop = changes.some((change: any) => change.type === 'position' && change.dragging === false);
    if (hasDragStop) {
      // Use setTimeout to ensure state is updated
      setTimeout(() => {
        setNodes((currentNodes) => {
          saveNodePositions(currentNodes);
          return currentNodes;
        });
      }, 0);
    }
  }, [onNodesChange, saveNodePositions, setNodes]);

  // Get pole suggestions based on search query
  const getPolesSuggestions = useCallback((): Pole[] => {
    if (!searchQuery.trim()) return [];

    const searchTerm = searchQuery.toLowerCase().trim();

    // Filter poles - prioritize exact matches and word boundaries
    const filtered = poles.filter((p: Pole) => {
      const poleName = p.name.toLowerCase();

      // Exact match (highest priority)
      if (poleName === searchTerm) return true;

      // Starts with search term
      if (poleName.startsWith(searchTerm)) return true;

      // Contains search term as whole word/number (separated by _, -, space)
      // For numbers, also check that it's not part of a larger number
      const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // Check if search term is a number
      const isNumber = /^\d+$/.test(searchTerm);

      if (isNumber) {
        // For numbers, require word boundary before and after (not part of larger number)
        // e.g., "15" should match "šachta_15" but NOT "šachta_35" or "šachta_150"
        const numberRegex = new RegExp(`(^|[_\\-\\s])${escapedTerm}($|[_\\-\\s])`, 'i');
        if (numberRegex.test(poleName)) return true;
      } else {
        // For text, allow partial matches within words
        const wordRegex = new RegExp(`(^|[_\\-\\s])${escapedTerm}`, 'i');
        if (wordRegex.test(poleName)) return true;

        // Also allow if search term is contained in the name
        if (poleName.includes(searchTerm)) return true;
      }

      return false;
    });

    // Sort by relevance: exact match > starts with > contains
    filtered.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();

      if (aName === searchTerm && bName !== searchTerm) return -1;
      if (aName !== searchTerm && bName === searchTerm) return 1;

      if (aName.startsWith(searchTerm) && !bName.startsWith(searchTerm)) return -1;
      if (!aName.startsWith(searchTerm) && bName.startsWith(searchTerm)) return 1;

      return aName.localeCompare(bName);
    });

    return filtered.slice(0, 10); // Limit to 10 suggestions
  }, [poles, searchQuery]);

  // Find path from OLT to a specific pole (by pole name)
  const findPathToPole = useCallback((poleName: string): Set<string> | null => {
    if (!poleName.trim()) return null;

    const searchTerm = poleName.toLowerCase().trim();

    // Find target pole by name - try exact match first, then partial
    let targetPole = poles.find((p: Pole) =>
      p.name.toLowerCase() === searchTerm
    );

    if (!targetPole) {
      // Try partial match as fallback
      targetPole = poles.find((p: Pole) =>
        p.name.toLowerCase().includes(searchTerm)
      );
    }

    if (!targetPole) {
      console.log(`[PathFilter] Pole "${poleName}" not found`);
      return null;
    }

    console.log(`[PathFilter] Found target pole: ${targetPole.name} (ID: ${targetPole.id})`);

    // Set to store all node IDs in the path
    const pathNodes = new Set<string>();

    // BFS to find path from OLT to target pole - track cables too
    const queue: Array<{ poleId: number; path: number[]; cablePath: number[] }> = [];
    const visited = new Set<number>();

    // Start from all OLT poles
    const oltPoles = poles.filter((p: Pole) => p.pole_type === 'olt');
    oltPoles.forEach((olt: Pole) => {
      queue.push({ poleId: olt.id, path: [olt.id], cablePath: [] });
      visited.add(olt.id);
    });

    let foundPath: number[] | null = null;
    let foundCablePath: number[] = [];

    while (queue.length > 0 && !foundPath) {
      const { poleId, path, cablePath } = queue.shift()!;

      if (poleId === targetPole.id) {
        foundPath = path;
        foundCablePath = cablePath;
        break;
      }

      // Find all cables going FROM this pole
      const outgoingCables = cables.filter((c: Cable) => c.from_pole_id === poleId);

      outgoingCables.forEach((cable: Cable) => {
        if (cable.to_pole_id && !visited.has(cable.to_pole_id)) {
          visited.add(cable.to_pole_id);
          queue.push({
            poleId: cable.to_pole_id,
            path: [...path, cable.to_pole_id],
            cablePath: [...cablePath, cable.id],
          });
        }
      });
    }

    if (!foundPath) {
      console.log(`[PathFilter] No path found from OLT to ${targetPole.name}`);
      return null;
    }

    console.log(`[PathFilter] Found path:`, foundPath.map(id => poles.find(p => p.id === id)?.name).join(' → '));
    console.log(`[PathFilter] Cable path:`, foundCablePath);

    // Determine which poles and cables to include based on showFullPath setting
    let polesToInclude: number[];
    let cablesToInclude: number[];

    if (showFullPath) {
      // Show full path: OLT → target pole → everything beyond
      // Use BFS to find all poles and cables reachable from target pole
      const reachableFromTarget = new Set<number>();
      const reachableCables = new Set<number>();
      const queue2 = [targetPole.id];
      const visited2 = new Set<number>([targetPole.id]);

      while (queue2.length > 0) {
        const currentPoleId = queue2.shift()!;
        reachableFromTarget.add(currentPoleId);

        const outgoing = cables.filter((c: Cable) => c.from_pole_id === currentPoleId);
        outgoing.forEach((cable: Cable) => {
          reachableCables.add(cable.id);
          if (cable.to_pole_id && !visited2.has(cable.to_pole_id)) {
            visited2.add(cable.to_pole_id);
            queue2.push(cable.to_pole_id);
          }
        });
      }

      // Combine path to target + everything beyond target
      const combined = foundPath.concat(Array.from(reachableFromTarget));
      polesToInclude = Array.from(new Set(combined));
      cablesToInclude = foundCablePath.concat(Array.from(reachableCables));
      console.log(`[PathFilter] Full path mode: ${polesToInclude.length} poles, ${cablesToInclude.length} cables (path + downstream)`);
    } else {
      // Show only path to target pole (OLT → target, nothing beyond)
      polesToInclude = foundPath;
      cablesToInclude = foundCablePath;
      console.log(`[PathFilter] Path-only mode: ${polesToInclude.length} poles, ${cablesToInclude.length} cables (OLT → target)`);
    }

    // Add cable IDs to pathNodes for filtering
    cablesToInclude.forEach(cableId => {
      pathNodes.add(`cable-${cableId}`);
    });

    // Add pole IDs to pathNodes (for reference, not used in filtering)
    polesToInclude.forEach(poleId => {
      pathNodes.add(`pole-${poleId}`);
    });

    console.log(`[PathFilter] Path includes ${cablesToInclude.length} cables and ${polesToInclude.length} poles`);

    console.log(`[PathFilter] Path includes ${pathNodes.size} nodes`);
    return pathNodes;
  }, [poles, cables, onus, showFullPath]);

  // Handle search
  const handleSearch = useCallback((poleName?: string) => {
    const searchTerm = poleName || searchQuery;
    const path = findPathToPole(searchTerm);
    setFilteredPath(path);
    setShowSuggestions(false);

    if (path) {
      console.log(`[PathFilter] Filter applied - showing ${path.size} nodes`);
      // Center the view on filtered nodes after a short delay to ensure nodes are rendered
      setTimeout(() => {
        fitView({ padding: 0.2, duration: 800 });
      }, 100);
    } else if (searchTerm.trim()) {
      alert(`Stĺpik "${searchTerm}" nebol nájdený`);
    }
  }, [searchQuery, findPathToPole, fitView]);

  // Clear filter
  const handleClearFilter = useCallback(() => {
    setSearchQuery('');
    setFilteredPath(null);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(0);
    console.log('[PathFilter] Filter cleared');
  }, []);

  // Handle search input change
  const handleSearchInputChange = useCallback((value: string) => {
    setSearchQuery(value);
    setShowSuggestions(value.trim().length > 0);
    setSelectedSuggestionIndex(0);
  }, []);

  // Handle suggestion click
  const handleSuggestionClick = useCallback((pole: Pole) => {
    setSearchQuery(pole.name);
    handleSearch(pole.name);
  }, [handleSearch]);

  // Handle keyboard navigation in suggestions
  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    const suggestions = getPolesSuggestions();

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && suggestions.length > 0) {
        const selectedPole = suggestions[selectedSuggestionIndex];
        handleSuggestionClick(selectedPole);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }, [getPolesSuggestions, selectedSuggestionIndex, showSuggestions, handleSuggestionClick, handleSearch]);

  // Generate hierarchical topology
  const generateTopology = useCallback(() => {

    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];
    let yOffset = 100;
    const xSpacing = 300;

    // Load saved positions
    const savedPositions = loadNodePositions();

    // Find OLT poles
    const oltPoles = poles.filter((p: Pole) => p.pole_type === 'olt');

    // Create OLT nodes (Level 0)
    oltPoles.forEach((olt: Pole, oltIndex: number) => {
      const portCount = olt.cassette?.olt_port_configs?.length || 1;

      for (let port = 1; port <= portCount; port++) {
        const nodeId = `olt-${olt.id}-port-${port}`;
        const portConfig = olt.cassette?.olt_port_configs?.find(
          (pc: any) => pc.port_number === port
        );

        newNodes.push({
          id: nodeId,
          type: 'olt',
          position: { x: 100, y: yOffset + oltIndex * 200 + (port - 1) * 150 },
          data: {
            label: `${olt.name} - Port ${port}`,
            port,
            power: portConfig?.power_dbm || 3.0,
            poleId: olt.id,
          },
        });
      }

      // Create OLT internal splitters (Level 0.5)
      if (olt.cassette?.splitters) {
        console.log(`[Topology2] OLT ${olt.name} has ${olt.cassette.splitters.length} splitters:`, olt.cassette.splitters);
        olt.cassette.splitters.forEach((splitter: any, splitterIndex: number) => {
          const splitterNodeId = `splitter-olt-${olt.id}-${splitterIndex}`;
          const splitterType = splitter.type as SplitterType;
          const outputCount = parseInt(splitterType.split(':')[1]);
          console.log(`[Topology2] Creating OLT splitter node: ${splitterNodeId}, type: ${splitterType}`);

          // Find port config for this splitter's input port
          const splitterPortConfig = olt.cassette?.olt_port_configs?.find(
            (pc: any) => pc.port_number === splitter.input_olt_port
          );

          newNodes.push({
            id: splitterNodeId,
            type: 'splitter',
            position: { x: 400, y: yOffset + oltIndex * 200 + splitterIndex * 150 },
            data: {
              label: `Splitter ${splitterType} (${olt.name})`,
              type: splitterType,
              loss: getSplitterLoss(splitterType),
              poleId: olt.id,
              splitterIndex,
              cascadeRatio: outputCount,
              editable: false,
              inputPower: splitterPortConfig?.power_dbm || 3.0,
              outputs: splitter.outputs,
            },
          });

          // Create edge from OLT port to internal splitter
          if (splitter.input_source_type === 'olt_port') {
            const oltNodeId = `olt-${olt.id}-port-${splitter.input_olt_port}`;
            newEdges.push({
              id: `edge-${oltNodeId}-${splitterNodeId}`,
              source: oltNodeId,
              sourceHandle: 'output',
              target: splitterNodeId,
              targetHandle: 'input',
              type: 'fiber',
              data: {
                loss: 0.1, // Internal connection - minimal loss
                distance: 1, // Internal - 1m
              },
            });
          } else if (splitter.input_source_type === 'splitter_output') {
            // Direct connection from another splitter in the same OLT cassette
            if (splitter.input_splitter_index !== undefined && olt.cassette?.splitters?.[splitter.input_splitter_index]) {
              const sourceSplitter = olt.cassette.splitters[splitter.input_splitter_index];
              const srcSplitterNodeId = `splitter-${olt.id}-${splitter.input_splitter_index}`;

              newEdges.push({
                id: `edge-${srcSplitterNodeId}-${splitterNodeId}`,
                source: srcSplitterNodeId,
                sourceHandle: `output-${splitter.input_splitter_output || 0}`,
                target: splitterNodeId,
                targetHandle: 'input',
                type: 'fiber',
                data: {
                  loss: 0.1, // Internal connection - minimal loss (not splitter loss!)
                  distance: 0, // Internal connection, no cable distance
                },
              });
            }
          }

          // Create edges from OLT splitter outputs to cables
          splitter.outputs?.forEach((output: any, outputIndex: number) => {
            if (output.cable_id && output.fiber) {
              const outputCable = cables.find((c: Cable) => c.id === output.cable_id);
              if (outputCable) {
                // Find target pole/splitter
                const targetPole = poles.find((p: Pole) => p.id === outputCable.to_pole_id);
                if (targetPole?.cassette?.splitters) {
                  // Find which splitter this connects to
                  targetPole.cassette.splitters.forEach((targetSplitter: any, targetIdx: number) => {
                    if (targetSplitter.input_source_type === 'cable' &&
                        targetSplitter.input_cable_id === output.cable_id &&
                        targetSplitter.input_fiber === output.fiber) {
                      const targetSplitterNodeId = `splitter-${targetPole.id}-${targetIdx}`;
                      newEdges.push({
                        id: `edge-${splitterNodeId}-${targetSplitterNodeId}`,
                        source: splitterNodeId,
                        sourceHandle: `output-${outputIndex}`,
                        target: targetSplitterNodeId,
                        targetHandle: 'input',
                        type: 'fiber',
                        data: {
                          loss: (outputCable.length_meters * 0.0003) || 0.3,
                          distance: outputCable.length_meters,
                          cableId: outputCable.id,
                          fiberNumber: output.fiber,
                        },
                      });
                    }
                  });
                }
              }
            }
          });
        });
      }
    });

    // Recursive function to trace back a fiber through passthrough/splice chain
    const traceBackFiber = (
      cableId: number,
      fiberNumber: number,
      visitedPoles: Set<number> = new Set()
    ): {
      sourceType: 'splitter' | 'olt' | 'none',
      sourcePoleId?: number,
      sourceSplitterIndex?: number,
      sourceOutputIndex?: number,
      sourceOltPort?: number,
      path: Array<{ type: 'passthrough' | 'splice', poleId: number, poleName: string, cableId: number }>,
      totalLoss: number,
      totalDistance: number
    } => {
      const cable = cables.find((c: Cable) => c.id === cableId);
      if (!cable) {
        console.log(`[TraceBack] Cable ${cableId} not found`);
        return { sourceType: 'none', path: [], totalLoss: 0, totalDistance: 0 };
      }

      const sourcePole = poles.find((p: Pole) => p.id === cable.from_pole_id);
      if (!sourcePole) {
        console.log(`[TraceBack] Source pole for cable ${cableId} not found`);
        return { sourceType: 'none', path: [], totalLoss: 0, totalDistance: 0 };
      }

      if (visitedPoles.has(sourcePole.id)) {
        console.log(`[TraceBack] Loop detected at pole ${sourcePole.name}`);
        return { sourceType: 'none', path: [], totalLoss: 0, totalDistance: 0 };
      }

      visitedPoles.add(sourcePole.id);

      const cableLoss = (parseFloat(cable.length_meters as any) || 0) * 0.0003;
      const cableDistance = parseFloat(cable.length_meters as any) || 0;

      console.log(`[TraceBack] Checking pole ${sourcePole.name} for cable ${cableId} fiber ${fiberNumber}`);

      // Check if this pole has a splitter that outputs to this cable+fiber
      if (sourcePole.cassette?.splitters) {
        console.log(`[TraceBack] Checking ${sourcePole.cassette.splitters.length} splitters at ${sourcePole.name}`);
        for (let i = 0; i < sourcePole.cassette.splitters.length; i++) {
          const splitter = sourcePole.cassette.splitters[i];
          console.log(`[TraceBack] Splitter ${i} outputs:`, splitter.outputs);
          const outputIndex = splitter.outputs?.findIndex(
            (out: any) => out.cable_id === cableId && out.fiber === fiberNumber
          );
          console.log(`[TraceBack] Looking for cable=${cableId} fiber=${fiberNumber}, found outputIndex=${outputIndex}`);
          if (outputIndex !== undefined && outputIndex >= 0) {
            console.log(`[TraceBack] ✅ Found splitter output at ${sourcePole.name}`);
            return {
              sourceType: 'splitter',
              sourcePoleId: sourcePole.id,
              sourceSplitterIndex: i,
              sourceOutputIndex: outputIndex,
              path: [],
              totalLoss: cableLoss,
              totalDistance: cableDistance
            };
          }
        }
      }

      // Check if this is OLT with direct port connection
      if (sourcePole.pole_type === 'olt' && (sourcePole.cassette as any)?.olt_ports) {
        const portIndex = (sourcePole.cassette as any).olt_ports.findIndex(
          (port: any) => port.cable_id === cableId && port.fiber === fiberNumber
        );
        if (portIndex >= 0) {
          console.log(`[TraceBack] ✅ Found OLT port at ${sourcePole.name}`);
          return {
            sourceType: 'olt',
            sourcePoleId: sourcePole.id,
            sourceOltPort: portIndex,
            path: [],
            totalLoss: cableLoss,
            totalDistance: cableDistance
          };
        }
      }

      // Check for passthrough (cable going OUT from this pole)
      if (sourcePole.cassette?.pass_through_cables) {
        for (const passThrough of sourcePole.cassette.pass_through_cables) {
          if (passThrough.to_cable_id === cableId) {
            const fiberConfig = passThrough.fibers?.find(
              (f: any) => f.fiber_number === fiberNumber && !f.is_tapped
            );
            if (fiberConfig) {
              console.log(`[TraceBack] Found passthrough at ${sourcePole.name}, continuing to cable ${passThrough.from_cable_id}`);
              const upstream = traceBackFiber(passThrough.from_cable_id, fiberNumber, visitedPoles);
              if (upstream.sourceType !== 'none') {
                return {
                  ...upstream,
                  path: [...upstream.path, { type: 'passthrough', poleId: sourcePole.id, poleName: sourcePole.name, cableId: cableId }],
                  totalLoss: upstream.totalLoss + cableLoss + 0.01, // passthrough bend loss
                  totalDistance: upstream.totalDistance + cableDistance
                };
              }
            }
          }
        }
      }

      // Also check if this pole is the TARGET of the cable and has passthrough/splice
      const targetPole = poles.find((p: Pole) => p.id === cable.to_pole_id);
      if (targetPole && targetPole.id !== sourcePole.id && !visitedPoles.has(targetPole.id)) {
        console.log(`[TraceBack] Also checking target pole ${targetPole.name} for passthrough/splice`);

        // Check for passthrough at target pole
        if (targetPole.cassette?.pass_through_cables) {
          for (const passThrough of targetPole.cassette.pass_through_cables) {
            if (passThrough.from_cable_id === cableId) {
              const fiberConfig = passThrough.fibers?.find(
                (f: any) => f.fiber_number === fiberNumber && !f.is_tapped
              );
              if (fiberConfig) {
                console.log(`[TraceBack] Found passthrough at target ${targetPole.name}, continuing to cable ${passThrough.to_cable_id}`);
                visitedPoles.add(targetPole.id);
                const upstream = traceBackFiber(passThrough.to_cable_id, fiberNumber, visitedPoles);
                if (upstream.sourceType !== 'none') {
                  return {
                    ...upstream,
                    path: [...upstream.path, { type: 'passthrough', poleId: targetPole.id, poleName: targetPole.name, cableId: passThrough.to_cable_id }],
                    totalLoss: upstream.totalLoss + cableLoss + 0.01,
                    totalDistance: upstream.totalDistance + cableDistance
                  };
                }
              }
            }
          }
        }

        // Check for splice at target pole
        if (targetPole.cassette?.splices) {
          for (const splice of targetPole.cassette.splices) {
            if (splice.from_cable_id === cableId && splice.from_fiber === fiberNumber) {
              console.log(`[TraceBack] Found splice at target ${targetPole.name}, continuing to cable ${splice.to_cable_id} fiber ${splice.to_fiber}`);
              visitedPoles.add(targetPole.id);
              const upstream = traceBackFiber(splice.to_cable_id, splice.to_fiber, visitedPoles);
              if (upstream.sourceType !== 'none') {
                return {
                  ...upstream,
                  path: [...upstream.path, { type: 'splice', poleId: targetPole.id, poleName: targetPole.name, cableId: splice.to_cable_id }],
                  totalLoss: upstream.totalLoss + cableLoss + (splice.loss_db || 0.1),
                  totalDistance: upstream.totalDistance + cableDistance
                };
              }
            }
          }
        }
      }

      // Check for splice
      if (sourcePole.cassette?.splices) {
        for (const splice of sourcePole.cassette.splices) {
          if (splice.to_cable_id === cableId && splice.to_fiber === fiberNumber) {
            // Check if splice is from OLT port
            if (splice.from_source_type === 'olt_port' && splice.from_olt_port !== undefined) {
              console.log(`[TraceBack] ✅ Found OLT port splice at ${sourcePole.name}, port ${splice.from_olt_port}`);
              return {
                sourceType: 'olt',
                sourcePoleId: sourcePole.id,
                sourceOltPort: splice.from_olt_port,
                path: [],
                totalLoss: cableLoss + (splice.loss_db || 0.1),
                totalDistance: cableDistance
              };
            }
            // Check if splice has valid from_cable_id and from_fiber
            else if (splice.from_cable_id && splice.from_fiber !== undefined) {
              console.log(`[TraceBack] Found splice at ${sourcePole.name}, continuing to cable ${splice.from_cable_id} fiber ${splice.from_fiber}`);
              const upstream = traceBackFiber(splice.from_cable_id, splice.from_fiber, visitedPoles);
              if (upstream.sourceType !== 'none') {
                return {
                  ...upstream,
                  path: [...upstream.path, { type: 'splice', poleId: sourcePole.id, poleName: sourcePole.name, cableId: cableId }],
                  totalLoss: upstream.totalLoss + cableLoss + (splice.loss_db || 0.1),
                  totalDistance: upstream.totalDistance + cableDistance
                };
              }
            }
          }
        }
      }

      console.log(`[TraceBack] No connection found at ${sourcePole.name} - fiber ends here`);
      return { sourceType: 'none', path: [], totalLoss: 0, totalDistance: 0 };
    };

    // Create Splitter nodes (Level 1, 2, 3...)
    const regularPoles = poles.filter((p: Pole) => p.pole_type !== 'olt');
    let splitterYOffset = 100;

    regularPoles.forEach((pole: Pole, poleIndex: number) => {
      if (pole.cassette?.splitters) {
        pole.cassette.splitters.forEach((splitter: any, splitterIndex: number) => {
          // Skip customer splitters if showCustomerSplitters is false
          if (!showCustomerSplitters && splitter.is_customer_splitter) {
            console.log(`[Topology2] Skipping customer splitter at ${pole.name}`);
            return;
          }

          const splitterNodeId = `splitter-${pole.id}-${splitterIndex}`;
          const splitterType = splitter.type as SplitterType;
          const outputCount = parseInt(splitterType.split(':')[1]);

          // Check if this is a customer cassette splitter (virtual pole)
          const isCustomerSplitter = pole.is_virtual === true;

          newNodes.push({
            id: splitterNodeId,
            type: 'splitter',
            position: { x: 400 + poleIndex * xSpacing, y: splitterYOffset },
            data: {
              label: `Splitter ${splitterType} (${pole.name})`,
              type: splitterType, // SplitterNode expects 'type', not 'splitterType'
              loss: getSplitterLoss(splitterType),
              poleId: pole.id,
              splitterIndex,
              cascadeRatio: outputCount, // TODO: Calculate actual cascade ratio
              editable: false,
              inputPower: undefined, // Will be calculated from upstream
              outputs: splitter.outputs,
              isCustomerSplitter, // Flag for customer cassette splitter
              backgroundColor: isCustomerSplitter ? '#d4edda' : undefined, // Light green for customer splitters
            },
          });

          // Create edge from input to splitter
          if (splitter.input_source_type === 'olt_port') {
            // Find the specific cable that connects to this splitter
            // IMPORTANT: Use input_cable_id if available, otherwise find by to_pole_id
            const connectingCable = splitter.input_cable_id
              ? cables.find((c: Cable) => c.id === splitter.input_cable_id)
              : cables.find((c: Cable) => c.to_pole_id === pole.id);

            if (connectingCable) {
              // Use optical path tracer to find the complete path from OLT to this splitter
              const pathResult = traceOpticalPath(connectingCable.id, splitter.input_fiber, poles, cables);

              if (pathResult.found) {
                // Find the source segment (OLT port or OLT splitter)
                const sourceSegment = pathResult.segments.find(seg =>
                  seg.type === 'olt_port' || seg.type === 'olt_splitter'
                );

                if (sourceSegment) {
                  // Extract OLT port number from description (format: "OLT name Port X (power dBm)")
                  let oltPort = splitter.input_olt_port || 1; // Use input_olt_port if available, otherwise default to 1
                  const portMatch = sourceSegment.description.match(/Port (\d+)/);
                  if (portMatch) {
                    oltPort = parseInt(portMatch[1], 10);
                  }

                  const oltPole = poles.find((p: Pole) => p.id === sourceSegment.poleId);
                  if (oltPole) {
                    // Check if OLT has internal splitters
                    let sourceNodeId = `olt-${oltPole.id}-port-${oltPort}`;
                    let sourceHandle = 'output';

                    if (oltPole.cassette?.splitters) {
                      // Find OLT splitter that outputs to this cable
                      oltPole.cassette.splitters.forEach((oltSplitter: any, oltSplitterIdx: number) => {
                        const outputIndex = oltSplitter.outputs?.findIndex(
                          (out: any) => out.cable_id === connectingCable.id
                        );
                        if (outputIndex !== undefined && outputIndex >= 0) {
                          // Connect from OLT splitter instead of OLT port
                          sourceNodeId = `splitter-olt-${oltPole.id}-${oltSplitterIdx}`;
                          sourceHandle = `output-${outputIndex}`;
                        }
                      });
                    }

                    // Convert segments to path format (filter out OLT segments, keep cable/passthrough/splice)
                    console.log(`[Topology2] 🔍 pathResult.segments for ${pole.name}:`, pathResult.segments);

                    const path = pathResult.segments
                      .filter(seg => seg.type === 'cable' || seg.type === 'passthrough' || seg.type === 'splice')
                      .map(seg => ({
                        type: seg.type as 'cable' | 'passthrough' | 'splice',
                        poleId: seg.poleId,
                        poleName: seg.poleName,
                        cableId: seg.cableId,
                        distance: seg.distance,
                      }));

                    console.log(`[Topology2] 🔍 Converted path for ${pole.name}:`, path);

                    const pathDesc = path.length > 0
                      ? path.map(p => `${p.type} at ${p.poleName}`).join(', ')
                      : 'direct';

                    const totalDistance = pathResult.segments.reduce((sum, seg) => sum + (seg.distance || 0), 0);

                    // IMPORTANT: Include cable ID and fiber number in edge ID to prevent duplicates
                    // when multiple cables connect from the same OLT port to different splitters
                    newEdges.push({
                      id: `edge-${sourceNodeId}-${splitterNodeId}-cable-${connectingCable.id}-fiber-${splitter.input_fiber}`,
                      source: sourceNodeId,
                      sourceHandle: sourceHandle,
                      target: splitterNodeId,
                      targetHandle: 'input',
                      type: 'fiber',
                      data: {
                        loss: pathResult.totalLoss,
                        distance: totalDistance,
                        cableId: connectingCable.id,
                        fiberNumber: splitter.input_fiber,
                        passThrough: path.some(p => p.type === 'passthrough'),
                        splice: path.some(p => p.type === 'splice'),
                        path: path,
                        pathDescription: pathDesc,
                      },
                    });
                  }
                } else {
                  console.log(`[Topology2] ⚠️ No OLT source found in path for ${pole.name}`);
                }
              } else {
                console.log(`[Topology2] ⚠️ Could not trace path for OLT port connection at ${pole.name}`);
              }
            }
          } else if (splitter.input_source_type === 'splitter_output') {
            // Direct connection from another splitter in the same cassette
            console.log(`[Topology2] Splitter ${splitterIndex} connected to splitter ${splitter.input_splitter_index} output ${splitter.input_splitter_output}`);

            if (splitter.input_splitter_index !== undefined && pole.cassette?.splitters?.[splitter.input_splitter_index]) {
              const sourceSplitter = pole.cassette.splitters[splitter.input_splitter_index];
              const srcSplitterNodeId = `splitter-${pole.id}-${splitter.input_splitter_index}`;

              newEdges.push({
                id: `edge-${srcSplitterNodeId}-${splitterNodeId}`,
                source: srcSplitterNodeId,
                sourceHandle: `output-${splitter.input_splitter_output || 0}`,
                target: splitterNodeId,
                targetHandle: 'input',
                type: 'fiber',
                data: {
                  loss: 0.1, // Internal connection - minimal loss (not splitter loss!)
                  distance: 0, // Internal connection, no cable distance
                  cableId: undefined,
                  fiberNumber: undefined,
                  passThrough: false,
                  splice: false,
                  path: [],
                  pathDescription: 'Internal cassette connection',
                },
              });
            }
          } else if (splitter.input_source_type === 'cable') {
            // Use optical path tracer to find source
            const pathResult = traceOpticalPath(splitter.input_cable_id, splitter.input_fiber, poles, cables);

            // Convert optical path result to topology format
            let traceResult: {
              sourceType: 'splitter' | 'olt' | 'none',
              sourcePoleId?: number,
              sourceSplitterIndex?: number,
              sourceOutputIndex?: number,
              sourceOltPort?: number,
              path: any[],
              totalLoss: number,
              totalDistance: number
            };
            if (!pathResult.found) {
              traceResult = {
                sourceType: 'none' as const,
                path: [],
                totalLoss: 0,
                totalDistance: 0
              };
            } else {
              // Find the source segment (splitter or OLT)
              const sourceSegment = pathResult.segments.find(seg =>
                seg.type === 'splitter' || seg.type === 'olt_port' || seg.type === 'olt_splitter'
              );

              if (sourceSegment?.type === 'olt_port' || sourceSegment?.type === 'olt_splitter') {
                // Extract OLT port number from description (format: "OLT name Port X (power dBm)")
                let oltPort = 1; // Default
                const portMatch = sourceSegment.description.match(/Port (\d+)/);
                if (portMatch) {
                  oltPort = parseInt(portMatch[1], 10);
                }

                // Convert segments to path format (filter out OLT segments, keep cable/passthrough/splice)
                const path = pathResult.segments
                  .filter(seg => seg.type === 'cable' || seg.type === 'passthrough' || seg.type === 'splice')
                  .map(seg => ({
                    type: seg.type as 'cable' | 'passthrough' | 'splice',
                    poleId: seg.poleId,
                    poleName: seg.poleName,
                    cableId: seg.cableId,
                    distance: seg.distance,
                  }));

                traceResult = {
                  sourceType: 'olt' as const,
                  sourcePoleId: sourceSegment.poleId,
                  sourceOltPort: oltPort,
                  path: path,
                  totalLoss: pathResult.totalLoss,
                  totalDistance: pathResult.segments.reduce((sum, seg) => sum + (seg.distance || 0), 0)
                };
              } else if (sourceSegment?.type === 'splitter') {
                // Find the actual output index from source splitter
                const sourcePole = poles.find((p: Pole) => p.id === sourceSegment.poleId);
                let actualOutputIndex = 0;
                let actualSplitterIndex = 0;

                if (sourcePole?.cassette?.splitters) {
                  // Find which splitter and which output connects to current splitter
                  // We need to find the FIRST cable in the path from source to current splitter
                  // This handles cases where there are passthrough/splice points in between

                  // Segments are in reverse order (from destination to source)
                  // Find the source splitter segment
                  const sourceSegmentIndex = pathResult.segments.findIndex(seg =>
                    seg.type === 'splitter' && seg.poleId === sourceSegment.poleId
                  );

                  // Find the first cable segment BEFORE the source splitter (in reverse order)
                  // This is the cable that comes OUT of the source splitter
                  const firstCableBeforeSource = pathResult.segments
                    .slice(0, sourceSegmentIndex)
                    .reverse()
                    .find(seg => seg.type === 'cable');

                  const targetCableId = firstCableBeforeSource?.cableId || splitter.input_cable_id;
                  const targetFiber = firstCableBeforeSource?.fiber || splitter.input_fiber;

                  sourcePole.cassette.splitters.forEach((srcSplitter: any, srcIdx: number) => {
                    srcSplitter.outputs?.forEach((output: any, outIdx: number) => {
                      if (output.cable_id === targetCableId &&
                          output.fiber === targetFiber) {
                        actualSplitterIndex = srcIdx;
                        actualOutputIndex = outIdx;
                      }
                    });
                  });
                }

                traceResult = {
                  sourceType: 'splitter' as const,
                  sourcePoleId: sourceSegment.poleId,
                  sourceSplitterIndex: actualSplitterIndex,
                  sourceOutputIndex: actualOutputIndex,
                  path: [],
                  totalLoss: pathResult.totalLoss,
                  totalDistance: pathResult.segments.reduce((sum, seg) => sum + (seg.distance || 0), 0)
                };
              } else {
                // No source found
                traceResult = {
                  sourceType: 'none' as const,
                  path: [],
                  totalLoss: 0,
                  totalDistance: 0
                };
              }
            }



            if (traceResult.sourceType === 'splitter') {
              // Found upstream splitter
              // Check if source is OLT splitter
              const sourcePole = poles.find((p: Pole) => p.id === traceResult.sourcePoleId);
              const isOltSplitter = sourcePole?.pole_type === 'olt';
              const srcSplitterNodeId = isOltSplitter
                ? `splitter-olt-${traceResult.sourcePoleId}-${traceResult.sourceSplitterIndex}`
                : `splitter-${traceResult.sourcePoleId}-${traceResult.sourceSplitterIndex}`;

              const pathDesc = pathResult.segments.length > 1
                ? pathResult.segments.map(seg => `${seg.type} at ${seg.poleName || 'unknown'}`).join(' → ')
                : 'direct';

              // Filter segments to only include those BETWEEN source and current splitter
              // Segments are in reverse order: [current] ... [intermediate] ... [source] ... [beyond source]
              const currentPoleId = pole.id;
              const sourcePoleId = traceResult.sourcePoleId;

              // Find the index of the source splitter segment
              const sourceSegmentIndex = pathResult.segments.findIndex(seg =>
                seg.type === 'splitter' && seg.poleId === sourcePoleId
              );

              // Only include segments BEFORE the source splitter (between current and source)
              const relevantSegments = pathResult.segments.slice(0, sourceSegmentIndex).filter(seg => {
                // Include cable segments
                if (seg.type === 'cable') {
                  return true;
                }
                // Include passthrough/splice that are NOT at source or current pole
                // (they are intermediate points in the path between the two splitters)
                if (seg.type === 'passthrough' || seg.type === 'splice') {
                  return seg.poleId !== currentPoleId && seg.poleId !== sourcePoleId;
                }
                // Exclude other segment types
                return false;
              });

              // Check if this is a reverse fiber (from splitter output, splice output, or passthrough)
              const isReverseFiber = poles.some(p => {
                // Check splitter outputs with reverse_direction flag
                const fromSplitter = p.cassette?.splitters?.some(s =>
                  s.outputs?.some(output =>
                    output.cable_id === splitter.input_cable_id &&
                    output.fiber === splitter.input_fiber &&
                    output.reverse_direction === true
                  )
                );

                // Check splice outputs with reverse_direction flag
                const fromSplice = p.cassette?.splices?.some(splice =>
                  splice.to_cable_id === splitter.input_cable_id &&
                  splice.to_fiber === splitter.input_fiber &&
                  splice.reverse_direction === true
                );

                // Check passthrough - signal goes FROM cable back to TO cable (reverse direction)
                const fromPassthrough = p.cassette?.pass_through_cables?.some(pt => {
                  // Signal comes on TO cable, goes back on FROM cable (reverse)
                  // This means input_cable_id is FROM cable, and signal came from TO cable
                  if (pt.from_cable_id === splitter.input_cable_id) {
                    const fiberConfig = pt.fibers?.find(f => f.fiber_number === splitter.input_fiber);
                    if (fiberConfig && !fiberConfig.is_tapped) {
                      // Check if signal actually comes from TO cable by looking at path segments
                      const hasPassthroughSegment = relevantSegments.some(seg =>
                        seg.type === 'passthrough' && seg.poleId === p.id
                      );
                      return hasPassthroughSegment;
                    }
                  }

                  return false;
                });

                return fromSplitter || fromSplice || fromPassthrough;
              });

              // Calculate total distance from all cable segments in the path
              const totalDistance = relevantSegments
                .filter(seg => seg.type === 'cable')
                .reduce((sum, seg) => sum + (seg.distance || 0), 0);

              // Calculate loss only for the cable segment between splitters
              // Fiber loss: 0.35 dB/km = 0.00035 dB/m
              const fiberLoss = totalDistance * 0.00035;

              // Add splice loss (0.03 dB per splice - fusion splice)
              const spliceCount = relevantSegments.filter(seg => seg.type === 'splice').length;
              const spliceLoss = spliceCount * 0.03;

              // Add passthrough loss (0.01 dB per passthrough - bend loss in splice closure)
              const passthroughCount = relevantSegments.filter(seg => seg.type === 'passthrough').length;
              const passthroughLoss = passthroughCount * 0.01;

              // Total loss on this cable segment (without splitter loss)
              const cableLoss = fiberLoss + spliceLoss + passthroughLoss;

              const edgeData = {
                loss: cableLoss,
                distance: totalDistance, // Use total distance from all cable segments
                cableId: splitter.input_cable_id,
                fiberNumber: splitter.input_fiber,
                passThrough: relevantSegments.some(seg => seg.type === 'passthrough'),
                splice: relevantSegments.some(seg => seg.type === 'splice'),
                path: relevantSegments.map(seg => ({
                  type: seg.type as 'passthrough' | 'splice' | 'cable',
                  poleId: seg.poleId,
                  poleName: seg.poleName,
                  cableId: seg.cableId,
                  distance: seg.distance,
                })),
                pathDescription: pathDesc,
                isReverseFiber: isReverseFiber,
              };



              newEdges.push({
                id: `edge-${srcSplitterNodeId}-${splitterNodeId}`,
                source: srcSplitterNodeId,
                sourceHandle: `output-${traceResult.sourceOutputIndex}`,
                target: splitterNodeId,
                targetHandle: 'input',
                type: 'fiber',
                data: edgeData,
              });
            } else if (traceResult.sourceType === 'olt') {
              // Found OLT port
              const oltNodeId = `olt-${traceResult.sourcePoleId}-port-${traceResult.sourceOltPort}`;

              const pathDesc = traceResult.path.length > 0
                ? traceResult.path.map(p => `${p.type} at ${p.poleName}`).join(', ')
                : 'direct';

              // IMPORTANT: Include cable ID and fiber number in edge ID to prevent duplicates
              // when multiple cables connect from the same OLT port to different splitters
              newEdges.push({
                id: `edge-${oltNodeId}-${splitterNodeId}-cable-${splitter.input_cable_id}-fiber-${splitter.input_fiber}`,
                source: oltNodeId,
                sourceHandle: 'output',
                target: splitterNodeId,
                targetHandle: 'input',
                type: 'fiber',
                data: {
                  loss: traceResult.totalLoss,
                  distance: traceResult.totalDistance,
                  cableId: splitter.input_cable_id,
                  fiberNumber: splitter.input_fiber,
                  passThrough: traceResult.path.some(p => p.type === 'passthrough'),
                  splice: traceResult.path.some(p => p.type === 'splice'),
                  path: traceResult.path,
                  pathDescription: pathDesc,
                },
              });
            } else {
              console.log(`[Topology2] ⚠️ No source found for splitter at ${pole.name}`);
            }
          }

          // Create ONU nodes for actual ONU devices connected to this splitter
          if (showONU) {
            // Find ONUs connected to this splitter
            const connectedONUs = onus.filter(
              (onu: any) => onu.pole_id === pole.id && onu.splitter_index === splitterIndex
            );



            connectedONUs.forEach((onu: any) => {
              const onuNodeId = `onu-${onu.id}`;
              const outputIndex = onu.splitter_port - 1; // Port numbers are 1-based

              // Calculate total loss to this ONU
              const splitterLoss = getSplitterLoss(splitterType);

              // Estimate cable loss (assuming ~50m from pole to customer)
              const estimatedCableLoss = 0.015; // 50m * 0.0003 dB/m
              const totalLoss = splitterLoss + estimatedCableLoss;

              newNodes.push({
                id: onuNodeId,
                type: 'onu',
                position: { x: 700 + poleIndex * xSpacing, y: splitterYOffset + outputIndex * 100 },
                data: {
                  label: onu.customer_name || onu.name,
                  poleId: pole.id,
                  onuId: onu.id,
                  totalLoss: totalLoss,
                  fiberColor: onu.fiber_color,
                  status: onu.status,
                },
              });

              newEdges.push({
                id: `edge-${splitterNodeId}-${onuNodeId}`,
                source: splitterNodeId,
                sourceHandle: `output-${outputIndex}`,
                target: onuNodeId,
                targetHandle: 'input',
                type: 'fiber',
                data: {
                  loss: estimatedCableLoss,
                  distance: 50, // Estimated distance from pole to customer
                  fiberColor: onu.fiber_color,
                },
              });
            });
          }

          splitterYOffset += 150;
        });
      }
    });

    // No virtual nodes - use direct edges
    const nodesWithVirtual = newNodes;
    const edgesWithVirtual = newEdges;

    // Apply hierarchical layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodesWithVirtual,
      edgesWithVirtual,
      {
        direction: 'LR', // Left to Right: OLT → Splitter → ONU
        nodeWidth: 220,
        nodeHeight: 120,
        rankSep: 200, // Horizontal spacing between levels
        nodeSep: 80,  // Vertical spacing between nodes
      }
    );

    // Apply saved positions if available
    const finalNodes = layoutedNodes.map(node => {
      const savedPos = savedPositions[node.id];
      if (savedPos) {
        return { ...node, position: savedPos };
      }
      return node;
    });

    // Calculate inputPower for all nodes (multi-pass for cascaded splitters)
    let nodesWithPower = [...finalNodes];

    // Multi-pass calculation (up to 10 passes for deep cascades)
    for (let pass = 0; pass < 10; pass++) {
      let hasChanges = false;

      nodesWithPower = nodesWithPower.map(node => {
        // OLT nodes already have power set
        if (node.type === 'olt') {
          return node;
        }

        // Calculate power for splitters and ONUs
        if ((node.type === 'splitter' || node.type === 'onu') && (node.data as any).inputPower === undefined) {
          // Find incoming edge
          const incomingEdge = layoutedEdges.find(edge => edge.target === node.id);
          if (incomingEdge) {
            // Find source node
            const sourceNode = nodesWithPower.find(n => n.id === incomingEdge.source);

            if (sourceNode) {
              let inputPower: number | undefined;

              // Calculate based on source type
              if (sourceNode.type === 'olt') {
                // Source is OLT - use OLT power directly
                const oltPower = (sourceNode.data as any).power as number;
                const cableLoss = (incomingEdge.data?.loss as number) || 0;
                inputPower = oltPower - cableLoss;
              } else if (sourceNode.type === 'splitter' && (sourceNode.data as any).inputPower !== undefined) {
                // Source is splitter - strata KONKRÉTNEHO výstupu (asymetria), podľa sourceHandle "output-<i>"
                const sourceInputPower = (sourceNode.data as any).inputPower as number;
                const sourceType = (sourceNode.data as any).type;
                const handle = (incomingEdge.sourceHandle as string) || '';
                const m = handle.match(/output-(\d+)/);
                const outIdx = m ? parseInt(m[1], 10) : 0;
                const sourceLoss = getSplitterOutputLoss(sourceType, outIdx);
                const sourceOutputPower = sourceInputPower - sourceLoss;
                const cableLoss = (incomingEdge.data?.loss as number) || 0;
                inputPower = sourceOutputPower - cableLoss;
              }

              if (inputPower !== undefined) {
                hasChanges = true;
                return {
                  ...node,
                  data: {
                    ...node.data,
                    inputPower,
                  },
                };
              }
            }
          }
        }
        return node;
      });

      // If no changes in this pass, we're done
      if (!hasChanges) break;
    }

    // Count connected outputs for each splitter (edges that go to other splitters)
    const nodesWithConnectedCounts = nodesWithPower.map(node => {
      if (node.type === 'splitter') {
        // Count how many edges go out from this splitter to other splitters (not ONUs)
        const outgoingEdges = layoutedEdges.filter(edge => {
          if (edge.source !== node.id) return false;
          // Check if target is a splitter (not ONU)
          const targetNode = nodesWithPower.find(n => n.id === edge.target);
          return targetNode && targetNode.type === 'splitter';
        });

        return {
          ...node,
          data: {
            ...node.data,
            connectedOutputsCount: outgoingEdges.length,
          },
        };
      }
      return node;
    });



    // Update edges with visibility flags
    const edgesWithVisibility = layoutedEdges.map(edge => ({
      ...edge,
      data: {
        ...edge.data,
        showCableInfo,
        showCableDistance,
        showPassthrough,
        showSplice,
        showFiberColor,
      },
    }));

    // Apply path filter if active
    let filteredNodes = nodesWithConnectedCounts;
    let filteredEdges = edgesWithVisibility;

    if (filteredPath) {
      console.log(`[PathFilter] Applying filter - showing ${filteredPath.size} items (cables + poles)`);

      // Step 1: Get path cables and poles
      const pathCables = Array.from(filteredPath)
        .filter(item => item.startsWith('cable-'))
        .map(item => parseInt(item.replace('cable-', '')));

      const pathPoles = Array.from(filteredPath)
        .filter(item => item.startsWith('pole-'))
        .map(item => parseInt(item.replace('pole-', '')));

      console.log(`[PathFilter] Path cables:`, pathCables);
      console.log(`[PathFilter] Path poles:`, pathPoles);

      if (pathPoles.length === 0) {
        console.error('[PathFilter] No poles in path');
        return;
      }

      // The last pole in the path is the target pole
      const targetPoleId = pathPoles[pathPoles.length - 1];
      console.log(`[PathFilter] Target pole ID: ${targetPoleId}`);

      // Step 2: Build edge lookup maps for efficient traversal
      const edgesByTarget = new Map<string, any[]>(); // target -> edges that end at this node

      edgesWithVisibility.forEach(edge => {
        if (!edgesByTarget.has(edge.target)) {
          edgesByTarget.set(edge.target, []);
        }
        edgesByTarget.get(edge.target)!.push(edge);
      });

      console.log(`[PathFilter] Built edge lookup map - ${edgesByTarget.size} targets`);

      // Step 3: Find all nodes at target pole (splitters, ONUs)
      const targetPoleNodes = nodesWithConnectedCounts.filter(node => {
        const nodeId = node.id;

        // Check if node belongs to target pole
        if (nodeId.startsWith('splitter-')) {
          // Regular splitter: splitter-{poleId}-{index}
          const match = nodeId.match(/^splitter-(\d+)-/);
          const poleId = match ? parseInt(match[1]) : null;
          return poleId === targetPoleId;
        }

        if (nodeId.startsWith('onu-')) {
          const onuId = parseInt(nodeId.replace('onu-', ''));
          const onu = onus.find((o: any) => o.id === onuId);
          return onu && onu.pole_id === targetPoleId;
        }

        return false;
      });

      console.log(`[PathFilter] Found ${targetPoleNodes.length} nodes at target pole:`, targetPoleNodes.map(n => n.id));

      // Step 4: Backward trace from each target pole node to find all nodes and edges on path
      const nodesOnPath = new Set<string>();
      const edgesOnPath = new Set<string>();

      const traceBackward = (startNodeId: string) => {
        const queue = [startNodeId];
        const visited = new Set<string>();

        console.log(`[PathFilter] Starting backward trace from ${startNodeId}`);

        while (queue.length > 0) {
          const nodeId = queue.shift()!;

          if (visited.has(nodeId)) continue;
          visited.add(nodeId);

          nodesOnPath.add(nodeId);

          // Find all edges that end at this node
          const incomingEdges = edgesByTarget.get(nodeId) || [];

          console.log(`[PathFilter]   Node ${nodeId} has ${incomingEdges.length} incoming edges`);

          incomingEdges.forEach(edge => {
            const edgeCableId = (edge.data as any)?.cableId;

            // For cable edges, only include if cable is in path
            if (edgeCableId !== undefined) {
              if (pathCables.includes(edgeCableId)) {
                console.log(`[PathFilter]     Cable edge ${edge.id} (cable ${edgeCableId}): IN PATH`);
                edgesOnPath.add(edge.id);
                nodesOnPath.add(edge.source);

                // Continue tracing from source
                if (!visited.has(edge.source)) {
                  queue.push(edge.source);
                }
              } else {
                console.log(`[PathFilter]     Cable edge ${edge.id} (cable ${edgeCableId}): NOT IN PATH - SKIPPED`);
              }
            } else {
              // Internal edge - always include and continue tracing
              console.log(`[PathFilter]     Internal edge ${edge.id}: INCLUDED`);
              edgesOnPath.add(edge.id);
              nodesOnPath.add(edge.source);

              // Continue tracing from source
              if (!visited.has(edge.source)) {
                queue.push(edge.source);
              }
            }
          });
        }
      };

      // Trace backward from all target pole nodes
      targetPoleNodes.forEach(node => {
        traceBackward(node.id);
      });

      console.log(`[PathFilter] After backward trace: ${nodesOnPath.size} nodes and ${edgesOnPath.size} edges`);

      // Step 6: Filter edges and nodes
      filteredEdges = edgesWithVisibility.filter(edge => edgesOnPath.has(edge.id));
      filteredNodes = nodesWithConnectedCounts.filter(node => nodesOnPath.has(node.id));

      console.log(`[PathFilter] Final: ${filteredNodes.length} nodes and ${filteredEdges.length} edges`);
    }

    setNodes(filteredNodes);
    setEdges(filteredEdges);
  }, [poles, cables, onus, showONU, showCustomerSplitters, showCableInfo, showCableDistance, showPassthrough, showSplice, showFiberColor, setNodes, setEdges, loadNodePositions, filteredPath]);

  // Generate topology on mount
  useEffect(() => {
    if (open) {
      generateTopology();
    }
  }, [open, generateTopology]);

  const exportFilename = (ext: string) =>
    `topology-${currentProject?.name || 'export'}-${new Date().toISOString().split('T')[0]}.${ext}`;

  // Export CELÉHO grafu ako SVG (nie len viditeľnej časti)
  const exportToSVG = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportFlowToSvg(getNodes(), exportFilename('svg'));
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export zlyhal. Skúste to znova.');
    } finally {
      setIsExporting(false);
    }
  }, [currentProject, getNodes]);

  // Export CELÉHO grafu ako PNG vo vysokom rozlíšení
  const exportToPNG = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportFlowToPng(getNodes(), exportFilename('png'));
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export zlyhal. Skúste to znova.');
    } finally {
      setIsExporting(false);
    }
  }, [currentProject, getNodes]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '95vw',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid #ddd',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
            🌐 Vláknová topológia - Hierarchický pohľad
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* React Flow */}
        <div style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            zoomOnScroll={true}
            zoomOnPinch={true}
            panOnScroll={false}
            panOnDrag={true}
            minZoom={0.1}
            maxZoom={4}
          >
            <Background />
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === 'olt') return '#2196f3';
                if (node.type === 'splitter') return '#ff9800';
                if (node.type === 'onu') return '#4caf50';
                return '#999';
              }}
            />

            {/* Path Filter Search */}
            <Panel position="top-left">
              <div style={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                padding: '12px',
                marginBottom: '10px',
                minWidth: '280px',
              }}>
                <div style={{ marginBottom: '8px', fontWeight: 600, fontSize: '13px', color: '#333' }}>
                  🔍 Filtrovať cestu k stĺpiku
                </div>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchInputChange(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="Názov stĺpika..."
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '13px',
                      }}
                    />
                    <button
                      onClick={() => handleSearch()}
                      style={{
                        padding: '6px 12px',
                        background: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                      title="Vyhľadať cestu"
                    >
                      🔍
                    </button>
                  </div>

                  {/* Suggestions dropdown */}
                  {showSuggestions && getPolesSuggestions().length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: '50px',
                      background: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      marginTop: '-8px',
                    }}>
                      {getPolesSuggestions().map((pole, index) => (
                        <div
                          key={pole.id}
                          onClick={() => handleSuggestionClick(pole)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            background: index === selectedSuggestionIndex ? '#e3f2fd' : 'white',
                            borderBottom: index < getPolesSuggestions().length - 1 ? '1px solid #f0f0f0' : 'none',
                            fontSize: '13px',
                            color: '#333',
                          }}
                          onMouseEnter={(e) => {
                            if (index !== selectedSuggestionIndex) {
                              e.currentTarget.style.background = '#f5f5f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (index !== selectedSuggestionIndex) {
                              e.currentTarget.style.background = 'white';
                            }
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>{pole.name}</div>
                          {pole.pole_type && (
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                              {pole.pole_type === 'olt' ? '🏢 OLT' :
                               pole.pole_type === 'pole' ? '📍 Stĺpik' :
                               pole.pole_type === 'building' ? '🏠 Budova' : pole.pole_type}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Path mode toggle */}
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  marginBottom: '8px',
                  padding: '4px',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                }}>
                  <button
                    onClick={() => {
                      setShowFullPath(false);
                      if (filteredPath) handleSearch(); // Re-apply filter with new mode
                    }}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      background: !showFullPath ? '#2196f3' : 'white',
                      color: !showFullPath ? 'white' : '#666',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 600,
                      transition: 'all 0.2s',
                    }}
                    title="Zobrazí len cestu od OLT po vybraný stĺpik"
                  >
                    📍 Po stĺpik
                  </button>
                  <button
                    onClick={() => {
                      setShowFullPath(true);
                      if (filteredPath) handleSearch(); // Re-apply filter with new mode
                    }}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      background: showFullPath ? '#2196f3' : 'white',
                      color: showFullPath ? 'white' : '#666',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 600,
                      transition: 'all 0.2s',
                    }}
                    title="Zobrazí celú cestu vrátane toho čo je za stĺpikom"
                  >
                    🌐 Celá trasa
                  </button>
                </div>

                {filteredPath && (
                  <button
                    onClick={handleClearFilter}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    ✕ Zrušiť filter
                  </button>
                )}
                {filteredPath && (
                  <div style={{
                    marginTop: '8px',
                    padding: '6px 8px',
                    background: '#e3f2fd',
                    borderRadius: '4px',
                    fontSize: '11px',
                    color: '#1976d2',
                  }}>
                    ℹ️ {showFullPath ? 'Celá trasa' : 'Cesta po stĺpik'} ({filteredPath.size} uzlov)
                  </div>
                )}
              </div>
            </Panel>

            <Panel position="top-right">
              <div style={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                color: '#333',
                minWidth: '180px',
              }}>
                {/* Schovaj info - collapsible */}
                <div style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb',
                }}>
                  <button
                    onClick={() => setShowInfoMenu(!showInfoMenu)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: showInfoMenu ? '#f3f4f6' : 'white',
                      color: '#374151',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>👁️ Schovaj info</span>
                    <span style={{ fontSize: '10px' }}>{showInfoMenu ? '▼' : '▶'}</span>
                  </button>
                  {showInfoMenu && (
                    <div style={{
                      padding: '12px',
                      background: 'white',
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' }}>
                        <input
                          type="checkbox"
                          checked={showONU}
                          onChange={(e) => setShowONU(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>Zobraziť ONU</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' }}>
                        <input
                          type="checkbox"
                          checked={showCustomerSplitters}
                          onChange={(e) => setShowCustomerSplitters(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>Zobraziť zákaznícke splittre</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
                        <input
                          type="checkbox"
                          checked={showCableInfo}
                          onChange={(e) => {
                            setShowCableInfo(e.target.checked);
                            // If hiding all cable info, also hide individual items
                            if (!e.target.checked) {
                              setShowCableDistance(false);
                              setShowPassthrough(false);
                              setShowSplice(false);
                              setShowFiberColor(false);
                            } else {
                              // If showing cable info, show all individual items
                              setShowCableDistance(true);
                              setShowPassthrough(true);
                              setShowSplice(true);
                              setShowFiberColor(true);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>Zobraziť info o kábli</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', marginLeft: '20px', opacity: showCableInfo ? 1 : 0.5 }}>
                        <input
                          type="checkbox"
                          checked={showCableDistance}
                          onChange={(e) => setShowCableDistance(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                          disabled={!showCableInfo}
                        />
                        <span>Zobraziť dĺžku kábla</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', marginLeft: '20px', opacity: showCableInfo ? 1 : 0.5 }}>
                        <input
                          type="checkbox"
                          checked={showPassthrough}
                          onChange={(e) => setShowPassthrough(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                          disabled={!showCableInfo}
                        />
                        <span>Zobraziť Pass-through</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', marginLeft: '20px', opacity: showCableInfo ? 1 : 0.5 }}>
                        <input
                          type="checkbox"
                          checked={showSplice}
                          onChange={(e) => setShowSplice(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                          disabled={!showCableInfo}
                        />
                        <span>Zobraziť Splice</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', marginLeft: '20px', opacity: showCableInfo ? 1 : 0.5 }}>
                        <input
                          type="checkbox"
                          checked={showFiberColor}
                          onChange={(e) => setShowFiberColor(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                          disabled={!showCableInfo}
                        />
                        <span>Zobraziť farbu vlákna</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Legend - collapsible */}
                <div style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb',
                }}>
                  <button
                    onClick={() => setShowLegend(!showLegend)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: showLegend ? '#f3f4f6' : 'white',
                      color: '#374151',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>📖 Legenda</span>
                    <span style={{ fontSize: '10px' }}>{showLegend ? '▼' : '▶'}</span>
                  </button>
                  {showLegend && (
                    <div style={{
                      padding: '12px',
                      background: 'white',
                      borderTop: '1px solid #e5e7eb',
                    }}>
                      <div style={{ fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div>🏢 OLT – ústredňa</div>
                        <div>🔀 Splitter - Signal divider</div>
                        <div>📡 ONU – koncové zariadenie zákazníka</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Export - collapsible */}
                <div style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb',
                }}>
                  <button
                    onClick={() => setShowExport(!showExport)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: showExport ? '#f3f4f6' : 'white',
                      color: '#374151',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>💾 Export</span>
                    <span style={{ fontSize: '10px' }}>{showExport ? '▼' : '▶'}</span>
                  </button>
                  {showExport && (
                    <div style={{
                      padding: '12px',
                      background: 'white',
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                      <button
                        onClick={exportToSVG}
                        disabled={isExporting}
                        style={{
                          padding: '8px 12px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: isExporting ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          width: '100%',
                          opacity: isExporting ? 0.6 : 1,
                        }}
                      >
                        {isExporting ? '⏳ Exportujem...' : '📄 SVG'}
                      </button>
                      <button
                        onClick={exportToPNG}
                        disabled={isExporting}
                        style={{
                          padding: '8px 12px',
                          background: '#8b5cf6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: isExporting ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          width: '100%',
                          opacity: isExporting ? 0.6 : 1,
                        }}
                      >
                        {isExporting ? '⏳ Exportujem...' : '🖼️ PNG'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Panel>
          </ReactFlow>
        </div>
      </div>
    </div>
  );
};

export const FiberTopologyDialog2: React.FC<FiberTopologyDialog2Props> = (props) => {
  return (
    <ReactFlowProvider>
      <FiberTopologyContent2 {...props} />
    </ReactFlowProvider>
  );
};

